import React from 'react'
import PropTypes from 'prop-types'
import classes from './index.module.css'
import Tooltip from '../Tooltip'

const REFRESHABLE_TOOLTIPS = {
  video_swapped:         'Refresh will use the new song video',
  orientation_changed:   'Refresh will use the new orientation',
  format_changed:        'Refresh will use the new format',
  duration_changed:      'Refresh will use the new video duration',
  video_offset_changed:  'Refresh will use the new video offset',
  lyrics_timing_changed: 'Refresh will use the new lyrics timing',
  line_timing_changed:   'Recreate Feel with updated timing',
  render_error:          'Refresh will retry the failed render',
  legacy_start_adj:      'Refresh will rebuild stored sync',
}

const ORPHAN_TOOLTIPS = {
  line_blank:         'The lyric this Feel uses is now blank. Create a new Feel if needed.',
  multi_line_changed: 'One of the lyrics this Feel uses has been edited. Create a new Feel to update.',
  text_changed:       'The lyric this Feel uses has been edited. Create a new Feel to update.',
}

const LOCKED_TOOLTIPS = {
  song_missing:         'Song record is missing',
  song_deleted:         'Song deleted',
  video_deleted:        'Song video deleted',
  feels_locked:         'Song is locked',
  no_song_video:        'No song video',
  no_song_lyrics:       'No song lyrics',
  song_lyrics_unsynced: 'Song lyric timing not synced — sync the song first',
}

function resolveTooltip(status, reason, isRefreshing) {
  if (isRefreshing) return 'Refreshing…'
  if (status === 'refreshable') return REFRESHABLE_TOOLTIPS[reason] || 'Refresh this feel'
  if (status === 'orphan')      return ORPHAN_TOOLTIPS[reason] || ORPHAN_TOOLTIPS.text_changed
  if (status === 'dead')        return 'Song no longer has this line.'
  if (status === 'locked')      return LOCKED_TOOLTIPS[reason] || `Locked: ${reason || 'unknown'}`
  return status
}

function resolveLabel(status) {
  if (status === 'orphan') return 'Lyric change'
  if (status === 'dead')   return 'Out of range'
  if (status === 'locked') return 'Locked'
  return status
}

const RefreshIconSvg = ({ size }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
)

RefreshIconSvg.propTypes = { size: PropTypes.number.isRequired }

export default function RefreshBadge({
  status,
  reason,
  variant = 'badge',
  size = 'sm',
  isRefreshing = false,
  disabled = false,
  onClick,
}) {
  const tooltip = resolveTooltip(status, reason, isRefreshing)

  if (variant === 'icon') {
    const isDisabled = disabled || isRefreshing
    const handleClick = (e) => {
      if (e && typeof e.stopPropagation === 'function') e.stopPropagation()
      if (!isDisabled && onClick) onClick(e)
    }
    const svgSize = size === 'md' ? 16 : 12
    return (
      <Tooltip text={tooltip}>
        <button
          type="button"
          aria-label="Refresh feel"
          onClick={handleClick}
          disabled={isDisabled}
          className={[
            classes.badge,
            classes.badge__icon,
            classes['badge__icon_' + size],
            classes.badge__refreshable,
            isRefreshing ? classes.badge__refreshing : '',
            isDisabled ? classes.disabled : classes.clickable,
          ].filter(Boolean).join(' ')}
        >
          <RefreshIconSvg size={svgSize} />
        </button>
      </Tooltip>
    )
  }

  return (
    <Tooltip text={tooltip}>
      <div
        className={[
          classes.badge,
          classes.badge__pill,
          classes['badge__' + status],
        ].filter(Boolean).join(' ')}
      >
        {resolveLabel(status)}
      </div>
    </Tooltip>
  )
}

RefreshBadge.propTypes = {
  status: PropTypes.oneOf(['refreshable', 'orphan', 'dead', 'locked', 'in_sync']).isRequired,
  reason: PropTypes.string,
  variant: PropTypes.oneOf(['icon', 'badge']),
  size: PropTypes.oneOf(['sm', 'md']),
  isRefreshing: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
}
